import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Send, AlertTriangle, Building2, Mail } from "lucide-react";
import { toast } from "sonner";

// Verzamelt alle e-mailstappen, ook binnen vertakkingen (takken).
function verzamelMailStappen(stappen, uit = []) {
  for (const s of stappen || []) {
    if (s?.type === "email") uit.push(s);
    const takken = s?.takken || {};
    for (const k of Object.keys(takken)) verzamelMailStappen(takken[k], uit);
  }
  return uit;
}

const REDEN_LABEL = {
  geen_email: "Deze klant heeft geen e-mailadres — zou worden overgeslagen.",
  opt_out: "Deze klant/contact staat op opt-out — zou geen mail ontvangen.",
};

export function VoorbeeldMailDialog({ open, onOpenChange, campagneId, stappen }) {
  const mailStappen = verzamelMailStappen(stappen);
  const [stapId, setStapId] = useState("");
  const [zoek, setZoek] = useState("");
  const [resultaten, setResultaten] = useState([]);
  const [org, setOrg] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zoekBusy, setZoekBusy] = useState(false);
  const [sturen, setSturen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (open) {
      setStapId(mailStappen[0]?.id || "");
      setZoek(""); setResultaten([]); setOrg(null); setData(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const laadVoorbeeld = useCallback(async (organisatie_id, stap_id) => {
    if (!organisatie_id || !stap_id) return;
    setBusy(true); setData(null);
    try {
      const { data } = await api.get(`/campagnes/${campagneId}/voorbeeld-mail`, { params: { organisatie_id, stap_id } });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Voorbeeld laden mislukt"); }
    finally { setBusy(false); }
  }, [campagneId]);

  const doeZoek = (term) => {
    setZoek(term);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!term || term.trim().length < 2) { setResultaten([]); return; }
      setZoekBusy(true);
      try {
        const { data } = await api.get(`/organisaties`, { params: { q: term.trim(), paged: true, page_size: 15 } });
        setResultaten(data.items || []);
      } catch { setResultaten([]); } finally { setZoekBusy(false); }
    }, 350);
  };

  const kiesOrg = (o) => { setOrg(o); setResultaten([]); setZoek(o.naam); laadVoorbeeld(o.id, stapId); };
  const wijzigStap = (v) => { setStapId(v); if (org) laadVoorbeeld(org.id, v); };

  const stuurNaarMij = async () => {
    if (!org || !stapId) return;
    setSturen(true);
    try {
      const { data } = await api.post(`/campagnes/${campagneId}/voorbeeld-mail/verstuur`, { organisatie_id: org.id, stap_id: stapId });
      toast.success(`Voorbeeld verstuurd naar ${data.naar} (klant: ${data.voorbeeld_organisatie})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    finally { setSturen(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="voorbeeld-mail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" />Voorbeeld voor een echte klant</DialogTitle>
          <DialogDescription>Kies een e-mailstap en een echte klant om de mail te zien zoals díe klant hem ontvangt.</DialogDescription>
        </DialogHeader>

        {mailStappen.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6" data-testid="voorbeeld-geen-stap">Deze campagne heeft nog geen e-mailstap. Voeg eerst een e-mailstap toe aan de flow.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">E-mailstap</Label>
                <Select value={stapId} onValueChange={wijzigStap}>
                  <SelectTrigger data-testid="voorbeeld-stap"><SelectValue placeholder="kies e-mailstap" /></SelectTrigger>
                  <SelectContent>{mailStappen.map((s, i) => <SelectItem key={s.id} value={s.id}>{s.naam || `E-mail ${i + 1}`}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 relative">
                <Label className="text-xs text-muted-foreground">Klant</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input value={zoek} onChange={e => doeZoek(e.target.value)} placeholder="zoek op naam, plaats of klantcode…" className="pl-8" data-testid="voorbeeld-klant-zoek" />
                  {zoekBusy && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
                </div>
                {resultaten.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg" data-testid="voorbeeld-klant-resultaten">
                    {resultaten.map(o => (
                      <button key={o.id} type="button" onClick={() => kiesOrg(o)} data-testid={`voorbeeld-klant-${o.id}`}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 border-b border-border/60 last:border-0">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="flex-1 truncate">{o.naam}</span>
                        <span className="text-[11px] text-muted-foreground shrink-0">{[o.klantcode, o.plaats, o.land].filter(Boolean).join(" · ")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin" />Voorbeeld renderen…</div>}

            {!busy && data && (
              <div className="space-y-3" data-testid="voorbeeld-resultaat">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-muted/50 border border-border rounded-lg px-3 py-2" data-testid="voorbeeld-infobalk">
                  <span><span className="text-muted-foreground">Aan:</span> <strong>{data.naar || "—"}</strong></span>
                  {data.contact?.naam && <span><span className="text-muted-foreground">Contact:</span> {data.contact.naam}</span>}
                  <span><span className="text-muted-foreground">Taal:</span> {(data.taal || "").toUpperCase()}</span>
                  <span><span className="text-muted-foreground">Afzender:</span> {data.afzender?.naam} &lt;{data.afzender?.email}&gt;</span>
                  <Badge variant="secondary" className="text-[10px]">{data.bron === "ontwerp" ? "HTML-ontwerp" : "Platte tekst"}</Badge>
                </div>

                {!data.ontvangbaar && (
                  <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="voorbeeld-waarschuwing">
                    <AlertTriangle className="h-4 w-4 shrink-0" />{REDEN_LABEL[data.ontvanger_reden] || "Deze klant zou worden overgeslagen."}
                  </div>
                )}

                <div>
                  <div className="text-xs text-muted-foreground mb-1">Onderwerp</div>
                  <div className="text-sm font-semibold border border-border rounded-lg px-3 py-2 bg-background" data-testid="voorbeeld-onderwerp">{data.onderwerp || <span className="text-muted-foreground font-normal">(leeg onderwerp)</span>}</div>
                </div>

                <div className="border border-border rounded-lg overflow-hidden bg-white">
                  <iframe title="E-mail voorbeeld" srcDoc={data.html} sandbox="" className="w-full h-[520px] bg-white" data-testid="voorbeeld-iframe" />
                </div>

                <div className="flex justify-end">
                  <Button onClick={stuurNaarMij} disabled={sturen} data-testid="voorbeeld-stuur-naar-mij">
                    {sturen ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Stuur dit voorbeeld naar mij
                  </Button>
                </div>
              </div>
            )}

            {!busy && !data && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="voorbeeld-leeg">Kies een klant om de mail te zien zoals die klant hem ontvangt.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
